import type { MediaInput, Condition, ModelInputContract, ParameterRule, Value } from '../../../../generated/modelInputContractTypes'
import type { CapabilitiesMap } from './generationPreflight'

export interface BlockReason {
  code: string
  params: Record<string, string | number>
}
export interface GenerationRequest {
  modelId: string
  referenceOrder?: ReadonlyArray<MediaInput["media"] | null>
  inputCounts: Readonly<Record<string, number | null>>
  parameters?: Readonly<Record<string, Value>>
  inputSeconds?: Readonly<Record<string, readonly number[]>>
  inputMetadata?: Readonly<Record<string, readonly Readonly<Record<string, Value>>[]>>
  phase?: 'preview' | 'execute'
  byomCapabilities?: CapabilitiesMap | null
}

function matches(conditions: readonly Condition[], request: GenerationRequest): boolean {
  return conditions.every((condition) => {
    const value = Object.prototype.hasOwnProperty.call(request.inputCounts, condition.field)
      ? request.inputCounts[condition.field] : request.parameters?.[condition.field]
    if (value == null) return false
    switch (condition.operator) {
      case 'eq': return value === condition.value
      case 'gt':
        if (typeof value !== 'number' || typeof condition.value !== 'number') {
          throw new Error(`Non-numeric contract condition: ${condition.field}`)
        }
        return value > condition.value
      default: throw new Error(`Unsupported contract operator: ${condition.operator}`)
    }
  })
}

export function parameterRuleAllows(rule: ParameterRule, value: Value): boolean {
  if (rule.disposition === 'provider-selected' || rule.disposition === 'advisory') return true
  if (rule.disposition === 'unsupported') return false
  if (rule.kind === 'number') {
    if (typeof value !== 'number' || !Number.isFinite(value)) return false
    if (rule.minimum !== null && value < rule.minimum) return false
    if (rule.maximum !== null && value > rule.maximum) return false
    if (rule.step !== null) {
      const quotient = (value - (rule.minimum ?? 0)) / rule.step
      if (Math.abs(quotient - Math.round(quotient)) > 1e-9) return false
    }
  }
  return rule.allowed.length === 0 || rule.allowed.includes(value)
}

/** Selectors and enforcement use the same conditional rule selection. */
export function resolveParameterRule(contract: ModelInputContract, parameter: string, request: GenerationRequest): ParameterRule | undefined {
  return contract.parameters.find((rule) => rule.parameter === parameter && matches(rule.when, request))
}

export function normalizeRequest(contract: ModelInputContract, request: GenerationRequest): GenerationRequest {
  const parameters = { ...request.parameters }
  const normalized = { ...request, parameters }
  const evaluated = new Set<string>()
  for (const rule of contract.parameters) {
    if (evaluated.has(rule.parameter) || !matches(rule.when, normalized)) continue
    evaluated.add(rule.parameter)
    const value = parameters[rule.parameter]
    if (value === undefined) continue
    if (rule.default_sentinels.includes(value)) {
      if (rule.default === null) throw new Error('Contract sentinel has no default')
      parameters[rule.parameter] = rule.default
    } else if (rule.case_insensitive && typeof value === 'string') {
      parameters[rule.parameter] = rule.allowed.find((item) => typeof item === 'string' && item.toLowerCase() === value.trim().toLowerCase()) ?? value
    }
  }
  return normalized
}

export function evaluateContract(contract: ModelInputContract, request: GenerationRequest, model: string): BlockReason[] {
  if (contract.schema_version !== 1) throw new Error(`Unsupported model input contract version: ${contract.schema_version}`)
  request = normalizeRequest(contract, request)
  const counts = request.inputCounts
  for (const input of contract.inputs) {
    const count = counts[input.port]
    if (count == null) {
      if ((request.phase ?? 'execute') === 'execute') throw new Error(`Incomplete generation inputs: ${input.port} was not evaluated`)
    } else if (!Number.isInteger(count) || count < 0) {
      throw new Error(`Invalid resolved input count for ${input.port}: ${count}`)
    }
  }
  const reasons: BlockReason[] = []
  for (const requirement of contract.requirements) {
    if (!requirement.first_reference_media || !matches(requirement.when, request)) continue
    const order = request.referenceOrder
    if (order?.length === 0 && requirement.minimum === 0) continue
    if (order !== undefined && order[0] !== requirement.first_reference_media) {
      reasons.push({ code: 'referenceFirstMediaRequired', params: { model, media: requirement.first_reference_media } })
    } else if (order?.includes(null)) {
      reasons.push({ code: 'referenceUnresolved', params: { model } })
    }
  }

  const prefixes = { image: 'referenceImages', video: 'referenceVideo', audio: 'referenceAudio' }
  for (const input of contract.inputs) {
    const count = counts[input.port]
    if (count == null) continue
    const prefix = prefixes[input.media]
    if (count > 0 && input.max_count === 0) reasons.push({ code: `${prefix}Unsupported`, params: { model } })
    else if (input.max_count !== null && count > input.max_count) {
      reasons.push({ code: `${prefix}TooMany`, params: { model, max: input.max_count, count } })
    }
  }
  const total = contract.inputs.reduce((sum, input) => sum + (counts[input.port] ?? 0), 0)
  if (contract.max_total_references !== null && total > contract.max_total_references) {
    reasons.push({ code: 'referenceTotalTooMany', params: { model, max: contract.max_total_references, total } })
  }
  for (const input of contract.inputs) {
    const seconds = request.inputSeconds?.[input.port] ?? []
    if (seconds.some((s) => !Number.isFinite(s) || s < 0)) throw new Error(`Invalid duration metadata for ${input.port}`)
    const minSeconds = input.min_seconds_each
    const maxSeconds = input.max_seconds_each
    if (minSeconds !== null && seconds.some((s) => s < minSeconds)) {
      reasons.push({ code: 'referenceTrimTooShort', params: { model, minSeconds } })
    }
    if (maxSeconds !== null && seconds.some((s) => s > maxSeconds)) {
      reasons.push({ code: 'referenceTrimTooLong', params: { model, maxSeconds } })
    }
    const sum = seconds.reduce((a, b) => a + b, 0)
    if (input.max_seconds_combined !== null && sum > input.max_seconds_combined) {
      reasons.push({ code: 'referenceTrimCombinedTooLong', params: { model, maxSeconds: input.max_seconds_combined, total: Math.round(sum * 100) / 100 } })
    }
    const metadata = request.inputMetadata?.[input.port] ?? []
    const count = counts[input.port]
    if (count != null && (metadata.length > count || (request.inputSeconds?.[input.port]?.length ?? 0) > count)) {
      throw new Error(`More metadata items than resolved inputs on ${input.port}`)
    }
    const evaluatedMetadata = new Set<string>()
    for (const rule of input.metadata_rules) {
      let candidates: readonly Readonly<Record<string, Value>>[] = metadata
      if (rule.aggregation === 'sum') {
        if (count == null || metadata.length !== count || metadata.some((item) => item[rule.parameter] === undefined)) continue
        const values = metadata.map((item) => item[rule.parameter])
        if (values.some((value) => typeof value !== 'number' || !Number.isFinite(value))) {
          throw new Error(`Non-numeric aggregate metadata on ${input.port}`)
        }
        candidates = [{ [rule.parameter]: values.reduce<number>((sum, value) => sum + Number(value), 0) }]
      }
      candidates.forEach((item, index) => {
        const value = item[rule.parameter]
        if (value === undefined || !matches(rule.when, { ...request, parameters: { ...request.parameters, ...item } })) return
        const key = `${index}:${rule.aggregation}:${rule.parameter}`
        if (evaluatedMetadata.has(key)) return
        evaluatedMetadata.add(key)
        if (!parameterRuleAllows(rule, value)) reasons.push({ code: rule.reason_code, params: {
          model, parameter: `${input.port}[${index}].${rule.parameter}`, value: String(value), allowed: allowedText(rule),
        } })
      })
    }
  }
  const evaluated = new Set<string>()
  for (const rule of contract.parameters) {
    if (evaluated.has(rule.parameter) || !matches(rule.when, request)) continue
    evaluated.add(rule.parameter)
    const value = request.parameters?.[rule.parameter]
    if (value === undefined || parameterRuleAllows(rule, value)) continue
    reasons.push({ code: rule.reason_code, params: { model, parameter: rule.parameter, value: String(value), allowed: allowedText(rule) } })
  }
  for (const rule of contract.requirements) {
    if (!matches(rule.when, request)) continue
    const values = rule.ports.map((port) => counts[port])
    if (values.some((value) => value == null)) continue
    const count = values.reduce<number>((sum, value) => sum + (value ?? 0), 0)
    if (rule.maximum !== null && count > rule.maximum) {
      const maximum = rule.maximum
      if (rule.ports.length === 1 && contract.inputs.some((input) => input.port === rule.ports[0] && input.max_count !== null && input.max_count <= maximum)) continue
      reasons.push({ code: 'inputCountTooMany', params: { model, port: rule.ports.join(', '), max: maximum, count } })
    } else if (count < rule.minimum) {
      reasons.push({ code: rule.reason_code, params: { model, port: rule.ports.join(', '), min: rule.minimum, count } })
    }
  }
  return reasons
}

function allowedText(rule: ParameterRule): string {
  if (rule.allowed.length > 0) return rule.allowed.join(', ')
  const range = `${rule.minimum ?? '-∞'}–${rule.maximum ?? '∞'}`
  return range + (rule.step !== null ? ` (step ${rule.step})` : '')
}
